import './FamilyPages.css';
import { publicHref } from '../lib/publicUrls';
import { Link } from 'react-router-dom';
import { Cake, CalendarDays, Check, ChevronLeft, Mail, PartyPopper, Phone, Timer, UserRound, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import InquiryJourney, { Field, Privacy, Tile } from '../components/InquiryJourney';
import { getAttribution } from '../lib/attribution';
import {
  PARTY_EXTRAS,
  PARTY_IMPORTANT_TERMS,
  PARTY_PACKAGES,
  PARTY_POLICY_GROUPS,
} from '../data/birthdayParty';

const initialForm = {
  hostName: '',
  hostEmail: '',
  hostPhone: '',
  childName: '',
  childAge: '',
  siblingCount: '0',
  guestCount: '',
  packageId: 'kids-zone',
  requestedDate: '',
  requestedTime: '',
  alternateDate: '',
  alternateTime: '',
  extras: [],
  notes: '',
};

const packageOf = (form) => PARTY_PACKAGES.find((partyPackage) => partyPackage.id === form.packageId);
const extrasOf = (form) => PARTY_EXTRAS.filter((extra) => form.extras.includes(extra.id));
const showDate = (date) => (date ? new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '');
const showTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
};
const when = (date, time) => [showDate(date), showTime(time)].filter(Boolean).join(' at ');

// The birthday inquiry as a journey like Book a Tour (owner direction, Sept 30). It sends exactly
// what the previous party form sent, including the same notes for Liz.
function toLead(form) {
  const selectedPackage = packageOf(form);
  const selectedExtras = extrasOf(form);
  const nameParts = form.hostName.trim().split(/\s+/);
  const firstName = nameParts.shift() || '';
  const lastName = nameParts.join(' ');
  const extrasText = selectedExtras.length
    ? selectedExtras.map((extra) => `${extra.label} ($${extra.price})`).join('; ')
    : 'None selected';

  const inquiryNotes = [
    `Birthday child: ${form.childName.trim()}`,
    `Turning age: ${form.childAge || 'Not provided'}`,
    `Number of siblings: ${form.siblingCount || '0'}`,
    `Expected children: ${form.guestCount || 'Not provided'}`,
    `Requested date/time: ${form.requestedDate} ${form.requestedTime || 'Time not specified'}`,
    `Alternate date/time: ${form.alternateDate || 'Not provided'} ${form.alternateTime || ''}`.trim(),
    `Package: ${selectedPackage?.name || form.packageId}`,
    `Requested add-ons: ${extrasText}`,
    `Additional notes: ${form.notes.trim() || 'None'}`,
  ].join('\n');

  return {
    first_name: firstName,
    last_name: lastName,
    email: form.hostEmail.trim(),
    phone: form.hostPhone.trim(),
    location: 'Brentwood',
    interests: ['Birthday Parties', selectedPackage?.name || 'Birthday Party'],
    source: 'birthday-party-inquiry',
    notes: inquiryNotes,
    ...getAttribution(),
  };
}

const earliestDate = () => new Date().toISOString().slice(0, 10);

const PARTY_STEPS = [
  {
    label: 'Package',
    question: () => 'Which party sounds right?',
    lede: 'Both parties are two hours. Add anything extra you’d like; nothing is booked until Liz confirms.',
    render: ({ form, set, toggle }) => (
      <div className="dv-journey__groups">
        <div className="dv-journey__group" role="group" aria-label="Party package">
          <p className="dv-journey__group-label">Party package</p>
          <div className="dv-journey__tiles dv-journey__tiles--large">
            {PARTY_PACKAGES.map((partyPackage) => (
              <Tile key={partyPackage.id} large icon={PartyPopper} label={partyPackage.name} price={partyPackage.price}
                detail={partyPackage.days} on={form.packageId === partyPackage.id} onClick={() => set('packageId', partyPackage.id)} />
            ))}
          </div>
        </div>
        <div className="dv-journey__group" role="group" aria-label="Requested add-ons">
          <p className="dv-journey__group-label">Add-ons (optional)</p>
          <div className="dv-journey__tiles dv-journey__tiles--large">
            {PARTY_EXTRAS.map((extra) => (
              <Tile key={extra.id} large label={extra.label} price={extra.price} detail={extra.packageNote}
                on={form.extras.includes(extra.id)} onClick={() => toggle('extras', extra.id)} />
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    label: 'Party',
    question: () => 'Tell us about the birthday star.',
    lede: 'So Liz can plan the right space and staff.',
    render: ({ form, fieldId, invalid, update }) => (
      <div className="dv-journey__fields dv-journey__fields--pair">
        <Field id={fieldId('childName')} label="Birthday child’s name *" icon={Cake} invalid={invalid('childName')}
          input={{ type: 'text', maxLength: 120, required: true, value: form.childName, onChange: update('childName') }} />
        <Field id={fieldId('childAge')} label="Turning age *" invalid={invalid('childAge')}
          input={{ type: 'number', min: 1, max: 18, inputMode: 'numeric', required: true, value: form.childAge, onChange: update('childAge') }} />
        <Field id={fieldId('guestCount')} label="Expected number of children *" icon={Users} invalid={invalid('guestCount')}
          input={{ type: 'number', min: 1, max: 100, inputMode: 'numeric', required: true, value: form.guestCount, onChange: update('guestCount') }} />
        <Field id={fieldId('siblingCount')} label="Number of siblings" invalid={invalid('siblingCount')}
          input={{ type: 'number', min: 0, max: 99, inputMode: 'numeric', value: form.siblingCount, onChange: update('siblingCount') }} />
      </div>
    ),
  },
  {
    label: 'Date',
    question: () => 'When would you like to celebrate?',
    lede: ({ form }) => `The ${packageOf(form)?.name} is available ${packageOf(form)?.days}. An alternate date helps Liz find a fit.`,
    render: ({ form, fieldId, invalid, update }) => (
      <div className="dv-journey__fields dv-journey__fields--pair">
        <Field id={fieldId('requestedDate')} label="Requested date *" icon={CalendarDays} invalid={invalid('requestedDate')}
          input={{ type: 'date', min: earliestDate(), required: true, value: form.requestedDate, onChange: update('requestedDate') }} />
        <Field id={fieldId('requestedTime')} label="Requested start time" invalid={invalid('requestedTime')}
          input={{ type: 'time', value: form.requestedTime, onChange: update('requestedTime') }} />
        <Field id={fieldId('alternateDate')} label="Alternate date" icon={CalendarDays} invalid={invalid('alternateDate')}
          input={{ type: 'date', min: earliestDate(), value: form.alternateDate, onChange: update('alternateDate') }} />
        <Field id={fieldId('alternateTime')} label="Alternate start time" invalid={invalid('alternateTime')}
          input={{ type: 'time', value: form.alternateTime, onChange: update('alternateTime') }} />
      </div>
    ),
  },
  {
    label: 'Host',
    question: () => 'Who’s hosting?',
    lede: 'Liz will use this to confirm availability and send the contract.',
    render: ({ form, fieldId, invalid, update }) => (
      <div className="dv-journey__fields">
        <Field id={fieldId('hostName')} label="Party host name *" icon={UserRound} invalid={invalid('hostName')}
          input={{ type: 'text', placeholder: 'John Doe', autoComplete: 'name', maxLength: 120, required: true, value: form.hostName, onChange: update('hostName') }} />
        <Field id={fieldId('hostEmail')} label="Email *" icon={Mail} invalid={invalid('hostEmail')}
          input={{ type: 'email', placeholder: 'john@example.com', autoComplete: 'email', maxLength: 200, required: true, value: form.hostEmail, onChange: update('hostEmail') }} />
        <Field id={fieldId('hostPhone')} label="Phone *" icon={Phone} invalid={invalid('hostPhone')}
          input={{ type: 'tel', placeholder: '(925) 555-0123', autoComplete: 'tel', maxLength: 40, required: true, value: form.hostPhone, onChange: update('hostPhone') }} />
      </div>
    ),
  },
  {
    label: 'Review',
    question: ({ firstName }) => `Here’s your party plan${firstName ? `, ${firstName}` : ''}.`,
    lede: 'Take a look, add any questions, then send it to Liz.',
    render: ({ form, fieldId, update }) => (
      <>
        <div className="dv-journey__fields">
          <Field id={fieldId('notes')} label="Questions or additional details (optional)" multiline
            input={{ maxLength: 2000, rows: 4, value: form.notes, onChange: update('notes') }} />
        </div>
        <Privacy>This inquiry does not reserve a date or collect payment. The birthday-party team will contact you to complete the contract.</Privacy>
      </>
    ),
  },
];

const partyRows = ({ form }) => {
  const selectedPackage = packageOf(form);
  const age = form.childAge ? `, turning ${form.childAge}` : '';
  return [
    ['Package', selectedPackage ? `${selectedPackage.name} · $${selectedPackage.price}` : '', 1],
    ['Add-ons', extrasOf(form).map((extra) => extra.label).join(' · ') || 'None', 1],
    ['Birthday child', form.childName.trim() ? `${form.childName.trim()}${age}` : '', 2],
    ['Children', form.guestCount ? `${form.guestCount} expected` : '', 2],
    ['Date', [when(form.requestedDate, form.requestedTime), form.alternateDate && `or ${when(form.alternateDate, form.alternateTime)}`].filter(Boolean).join(' · '), 3],
    ['Host', [form.hostName.trim(), form.hostEmail, form.hostPhone].filter(Boolean).join(' · '), 4],
  ];
};

const missingDetail = (form) => (
  !form.childName.trim() || !form.childAge || !form.guestCount ? 2
    : !form.requestedDate ? 3
      : !form.hostName.trim() || !form.hostEmail || !form.hostPhone ? 4 : -1
);

const BirthdayPartyPage = () => {
  return (
    <div className="dv-family dv-birthday" data-testid="birthday-party-page">
      <SEO
        title="Birthday Party Inquiry | Delta Valley Health Club"
        description="Request a Kids Zone or Kids Zone & Beach birthday party at Delta Valley Health Club in Brentwood."
        keywords="kids birthday party Brentwood, pool party Brentwood, Kids Zone party"
      />

      <section className="dv-family__text-hero">
        <div className="container-custom max-w-5xl">
          <Link to={publicHref("/programs/kids-and-family")} className="mb-5 inline-flex items-center text-sm text-blue-100 hover:text-white">
            <ChevronLeft size={16} className="mr-1" /> Back to Kids & Family
          </Link>
          <h1 className="dv-size-heading text-4xl font-bold md:text-5xl">Birthday Party Inquiry</h1>
          <p className="dv-size-lede mt-4 max-w-3xl text-lg text-blue-100">
            Review the current packages and policies, then send Liz your preferred party details.
            This form is an inquiry and does not reserve a date or collect payment.
          </p>
        </div>
      </section>

      <section className="py-14">
        <div className="container-custom max-w-5xl">
          <h2 className="dv-size-heading text-3xl font-bold">Party packages</h2>
          <div className="mt-7 grid gap-6 md:grid-cols-2">
            {PARTY_PACKAGES.map((partyPackage) => (
              <Card key={partyPackage.id} className="dv-family__panel dv-family__package">
                <CardContent className="p-7">
                  <div className="dv-family__package-heading">
                    <div>
                      <h3 className="dv-size-subheading text-2xl font-bold">{partyPackage.name}</h3>
                      <p className="mt-1 text-sm font-semibold text-[#005A94]">{partyPackage.days}</p>
                    </div>
                    <div className="dv-size-subheading text-2xl font-bold text-[#0072BB]">${partyPackage.price}</div>
                  </div>
                  <p className="mt-4 font-semibold">{partyPackage.duration}</p>
                  <ul className="mt-4 space-y-2 text-gray-700">
                    {partyPackage.includes.map((item) => (
                      <li key={item} className="dv-size-body flex gap-2">
                        <Check size={17} className="mt-0.5 shrink-0 text-[#0072BB]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="dv-family__panel mt-7">
            <CardContent className="p-7">
              <h3 className="dv-size-subheading text-2xl font-bold">Optional add-ons</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {PARTY_EXTRAS.map((extra) => (
                  <div key={extra.id} className="flex items-start justify-between gap-4 border-b py-2">
                    <div>
                      <div className="font-medium">{extra.label}</div>
                      {extra.packageNote && <div className="text-xs text-gray-500">{extra.packageNote}</div>}
                    </div>
                    <div className="shrink-0 font-bold text-[#0072BB]">${extra.price}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="container-custom max-w-5xl">
          <h2 className="dv-size-heading text-3xl font-bold">Important party information</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Card className="dv-family__panel">
              <CardContent className="p-6">
                <h3 className="dv-size-subheading text-xl font-bold">Booking terms</h3>
                <ul className="mt-4 space-y-2 text-sm leading-relaxed text-gray-700">
                  {PARTY_IMPORTANT_TERMS.map((term) => <li className="dv-size-body" key={term}>• {term}</li>)}
                </ul>
              </CardContent>
            </Card>
            <div className="space-y-3">
              {PARTY_POLICY_GROUPS.map((group) => (
                <details key={group.title} className="dv-family__policy">
                  <summary className="cursor-pointer font-bold text-[#005A94]">{group.title}</summary>
                  <ul className="mt-4 space-y-2 text-sm leading-relaxed text-gray-700">
                    {group.items.map((item) => <li className="dv-size-body" key={item}>• {item}</li>)}
                  </ul>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <InquiryJourney
        id="birthday-party-inquiry" tone="gold" testId="birthday-party-journey" progressLabel="Party planning steps"
        welcome={{
          eyebrow: 'Request a birthday party',
          title: ['Plan a party they’ll remember.', 'Send Liz your details in about two minutes.'],
          lede: 'Share your preferred date and package. Liz will confirm availability before any contract or deposit is required.',
          promises: [[Timer, 'About two minutes'], [Check, 'No payment today'], [UserRound, 'Liz confirms availability']],
          photo: 'C05',
        }}
        steps={PARTY_STEPS}
        initial={initialForm}
        payload={toLead}
        missing={missingDetail}
        plan={{ title: 'Your party plan', rows: partyRows }}
        startLabel="Start Planning My Party"
        submitLabel="Send Birthday Party Inquiry"
        success={{
          title: 'Inquiry received',
          heading: ({ firstName }) => `Party request sent${firstName ? `, ${firstName}` : ''}.`,
          lede: 'Liz and the Delta Valley Kids team received your request and will follow up about availability and the contract.',
          next: [
            [Mail, 'Liz reaches out', 'To confirm availability for your date.'],
            [Check, 'Confirm the details', 'The contract and any deposit come only after Liz confirms.'],
            [PartyPopper, 'Celebrate', 'Hosts may set up 30 minutes before the party begins.'],
          ],
          actions: (
            <>
              <a className="dv-button dv-button--light" href="tel:+19252402990"><Phone size={16} aria-hidden="true" /> (925) 240-2990</a>
              <a className="dv-button dv-button--outline-light" href="mailto:lizh@deltavac.com"><Mail size={16} aria-hidden="true" /> lizh@deltavac.com</a>
            </>
          ),
        }}
      />
    </div>
  );
};

export default BirthdayPartyPage;
